import { Base2Presenter } from "core/presenter";
import {
  ProductRepository,
  RuntimeRepository,
  BarcodeRepository
} from "core/repository";
import { xml2js } from "xml-js";
import Big from "big.js";
import _ from "lodash";

class EntryByXMLPresenter extends Base2Presenter {
  initialState() {
    return {
      loading: false,
      fileStatus: "absent",
      file: "",
      products: [],
      xmlBarcodes: [],
      sellableSearchOptions: {
        online: true,
        repository: ProductRepository,
        order: [{ field: "name", direction: "ASC" }],
        pagination: { page: 1, perPage: 25 },
        filter: filter => this.getSellableFilter(filter)
      },
      page: 1,
      import_permission: true,
      importSellableCode: false,
      progress: 0,
      logsDialogOpen: false,
      importStatusLoading: false,
      logsMessages: [],
      logsFields: []
    };
  }

  timeout(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async onStart() {
    await this.checkImportProgress(true);
  }

  async checkImportProgress(first = false) {
    this.enqueue({ importStatusLoading: true });

    let result = await this.getImportStatus();

    if (result.data) {
      while (result.data.status === "in_progress") {
        result = await this.getImportStatus();
        const data = result.data;

        if (!result.success || result.fatal) {
          this.sendNotification({
            type: "error",
            title: "Ooops",
            content:
              "Ocorreu um problema na importação. Verifique sua conexão e tente novamente ou entre em contato com o suporte."
          });
        } else {
          if (data.status === "in_progress") {
            this.enqueue({ import_permission: false, progress: data.progress });
          } else {
            this.handleErrors(data);
            this.enqueue({ import_permission: true, progress: 0 });
          }
        }

        this.enqueue({ importStatusLoading: false });
        await this.timeout(3000);
      }

      if (result.data.status === "done" && !first) {
        this.navigateTo("app.registration.product");
      }

      this.handleErrors(result.data);
      this.enqueue({ importStatusLoading: false });
    } else {
      this.enqueue({ importStatusLoading: false });
    }
  }

  onImportSellableCodeChange () {
    this.enqueue({ importSellableCode: !this.viewModel.importSellableCode })
  }

  handleErrors(data) {
    if (["done_with_error", "error"].includes(data.status)) {
      const fields = [];
      const messages = [];

      _.map(data.logs, err => {
        messages.push(err.message);
        _.map(err.fields, (value, key) => {
          fields.push(`${key} ${value[0]}`);
        });
      });

      this.enqueue({
        logsDialogOpen: true,
        logsMessages: messages,
        logsFields: fields
      });
    }
  }

  async getImportStatus() {
    return await ProductRepository.getImportStatus();
  }

  onGoBack() {
    this.navigateTo("app.registration.product.inventory.modify");
  }

  onPageChange(page) {
    this.viewModel.page = page;
    this.relateProducts();
    this.enqueue({ page });
  }

  async getAllBarcodes(barcodes) {
    return await ProductRepository.getAllBarcodes(barcodes, this.viewModel.page);
  }

  async relateProducts () {
    const { xmlBarcodes } = this.viewModel

    this.enqueue({ loading: true })

    const formattedBarcodes = xmlBarcodes.map(barcode => barcode.replace(/-/g,""))
    const barcodes = await this.getAllBarcodes(formattedBarcodes)

    const sellablesUuidList = barcodes.map(barcode => barcode.sellable_uuid)
    const sellables = await this.getSellablesByUuid(sellablesUuidList)

    if (barcodes.length === 0 || sellables.length === 0) {
      this.enqueue({ loading: false })
      return
    }

    this.enqueue({ loading: false, products: this.updateProducts(barcodes, sellables) })
  }

  updateProducts(barcodes, sellables) {
    const { products } = this.viewModel

    return products.map(product => {
      const code = barcodes.find(barcode => barcode.code === product.barcode)

      if (!code) return product

      return {
        ...product,
        importSellable: this.findSellable(sellables, code)
      }
    })
  }

  findSellable(sellables, code) {
    const foundSellable = sellables.find(sellable =>  (
      !sellable.deleted && sellable.uuid === code.sellable_uuid
    ))

    return foundSellable || { name: "", uuid: null}
  }

  async getSellablesByUuid(uuidList) {
    return await ProductRepository.getSellablesByUuid(uuidList);
  }

  onFileSelect(file) {
    const reader = new FileReader();

    reader.onload = event => {
      const content = event.target.result;

      try {
        const parsedXml = xml2js(content, { compact: true });
        const xmlRoot = parsedXml.nfeProc ? parsedXml.nfeProc : parsedXml;
        const rawProducts = xmlRoot.NFe.infNFe.det;
        const products = (rawProducts instanceof Array
          ? rawProducts
          : [rawProducts]
        ).map(xmlDet => xmlDet.prod);

        this.enqueue({
          file: content,
          products: products.map((xmlProduct, index) => ({
            index,
            productCode: xmlProduct.cProd._text,
            barcode: xmlProduct.cEAN._text,
            name: xmlProduct.xProd._text,
            quantity: Big(xmlProduct.qCom._text)
              .times(100)
              .round(),
            unitLabel: xmlProduct.uCom._text,
            value: Big(xmlProduct.vProd._text)
              .times(100)
              .round(),
            importSellable: { name: "", uuid: null },
            errors: {}
          })),
          xmlBarcodes: products.map(product => product.cEAN._text),
          fileStatus: "valid",
          loading: false
        });
      } catch (error) {
        this.enqueue({
          fileStatus: "invalid",
          loading: false
        });
      }
    };

    reader.readAsBinaryString(file[0]);

    reader.onloadend = () => {
      this.relateProducts()
    }
  }

  async getSellableFilter(query) {
    const currentUser = await this.mRuntime.get(
      RuntimeRepository.props.currentUser
    );

    return [
      { connector: "AND", field: "code", operation: "LIKE", value: query },
      { connector: "OR", field: "name", operation: "LIKE", value: query },
      { field: "type", operation: "EQ", value: "product" },
      {
        connector: "OR",
        complex: true,
        fn: async model => {
          const result = await BarcodeRepository.findFirst({
            filters: [
              { field: "sellable_uuid", operation: "EQ", value: model.uuid },
              { field: "code", operation: "LIKE", value: query }
            ]
          });

          return !!result;
        }
      },
      { connector: "AND", field: "deleted", operation: "EQ", value: false },
      {
        connector: "AND",
        field: "company_uuid",
        operation: "EQ",
        value: currentUser.company_uuid
      }
    ];
  }

  onUpdateItem(index, field, value) {
    this.enqueue(vm => {
      const productCode = vm.products[index]["productCode"];

      if (vm.products.some(product => product.productCode === productCode)) {
        const products = vm.products
          .filter(product => product.productCode === productCode)
          .map((product, index, filteredProducts) => {
            if (filteredProducts.length > 1) {
              this.sendNotification({
                type: "success",
                content:
                  "Percebemos que haviam produtos iguais a esse e relacionamos automaticamente pra você!"
              });
            }

            product[field] = value;
            return product;
          });

        return products;
      }

      vm.products[index][field] = value;
      return { products: vm.products };
    });
  }

  parsingToObject(products) {
    const productLinks = products.reduce((obj, product) => {
      const { importSellable, productCode } = product;
      const { uuid } = importSellable;

      if (uuid) {
        return { ...obj, [uuid]: productCode };
      }

      return obj;
    }, {});

    return productLinks;
  }

  async onCancel() {
    this.enqueue(this.initialState());
  }

  async onImport() {
    this.enqueue({ loading: true });

    const productLinks = this.parsingToObject(this.viewModel.products);

    const importParams = {
      file: this.viewModel.file,
      import_name: "sellable",
      sellable_links: productLinks,
      import_sellable_code: this.viewModel.importSellableCode
    };

    const importResult = await ProductRepository.importFile(importParams);

    if (importResult.success) {
      this.sendNotification({
        type: "success",
        content: "Importação realizada com sucesso!",
        onHide: async () => {
          await this.checkImportProgress();
        }
      });
    } else if (importResult.errors) {
      this.sendNotification({
        type: "error",
        title: "Oops...",
        content:
          "Ocorreram erros durante a importação. Verifique-os e tente novamente ou entre em contato com o suporte."
      });
    } else {
      this.sendNotification({
        type: "error",
        title: "Oops...",
        content:
          "Ocorreu um problema na importação. Verifique sua conexão e tente novamente ou entre em contato com o suporte."
      });
    }

    await this.checkImportProgress();
    this.enqueue({ loading: false });
  }

  onLogsDialogClose() {
    this.enqueue({ logsDialogOpen: false });
  }

  onLogsDialogOpen() {
    this.enqueue({ logsDialogOpen: true });
  }
}

export default EntryByXMLPresenter;



// WEBPACK FOOTER //
// ./src/components/app/registration/product/inventory/modify/xml/presenter.js